import React from 'react';
import { Text, Grid, Container, Link, Stack, Flex } from 'basis';
import { StyledUL } from 'components';
import {
  config,
  OutcomeApprovedSourcesAu,
  PRODUCT_DAVID_JONES,
  PRODUCT_CODE_AU_GEM,
  PRODUCT_CODE_DAVID_JONES_PREMIERE,
  PRODUCT_CODE_DAVID_JONES_PRESTIGE,
} from '_config';
import { useDataLayerAndAnalyticsLinks } from 'hooks';
import { displayCreditLimit, byCountry, isDJ, byCard, getProductName, isGemAU } from 'utils';
import styled from '@emotion/styled';
import DownloadApple from 'images/download-apple.svg';
import DownloadGoogle from 'images/download-google.svg';

import appleGoogleSamsungPay from 'images/apple-google-samsung-pay.png';
import gemNzCard from 'images/gem-nz-card.webp';
import ellipse from 'images/ellipse.svg';
import { getRatesAndFees } from 'featureToggles';
import { DividerWithText } from 'components/divider';
import { ShadowedContainer } from '../../../components/info-card/InformationCard';
import { getAuCardOutcomeApprovedNextSteps, getCardImageMobile } from './auContent';
import { whatHappensNextStepsNzV2 } from './nzV2Content';

const StyledNumberListItem = styled.p`
  display: inline-block;
  margin-block-start: inherit;
  text-align: center;
  line-height: 24px;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  background: ${isDJ() ? 'black' : '#0B2374'};
  border-radius: 24px;
  width: 24px;
  height: 24px;
  margin-block-end: 0;
`;

const StyledStrong = styled.strong`
  color: ${isDJ() ? 'black' : '#0B2374'};
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
`;

const TopImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: auto;

  & img {
    height: ${byCard({
      [PRODUCT_CODE_AU_GEM]: '220px',
      [PRODUCT_DAVID_JONES]: '133px',
      default: '220px',
    })};
  }
`;

const TextAlignWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  ${props => `
      ${`@media (max-width: ${props.theme?.screenSize?.xs || '375px'})`} {
        padding: 16px 0;
      }
      ${`@media (min-width: ${props.theme?.screenSize?.sm || '576px'})`} {
        padding: 0 16px;
        h2 {
          line-height: 28px;
        }
      }
      `}
`;

const IconTextWrapper = styled.div`
  display: flex;
  margin-left: 16px;
  img {
    height: 28px;
  }
`;

const RightTextWrapper = styled.div`
  display: flex;
  margin-left: 10px;
  width: 90%;
`;

const ImageHeaderWrapper = styled.div`
  display: flex;
  margin-bottom: 24px;
`;

const TextHeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-left: 12px;
  width: 90%;
`;

const NumberedListWrapper = styled.div`
  display: flex;
  margin-bottom: 4px;
`;

const NumberedTextWrapper = styled.div`
  display: flex;
  margin-left: 12px;
  width: 90%;
`;

const SmallContainerWrapper = styled.div`
  background: ${props => props.theme?.colors?.specialBackground || '#f5f7fb'};
  ${props => `
  ${`@media (max-width: ${props.theme?.screenSize?.xs || '375px'})`} {
    margin-right: 6px;
  `}
`;

const DownloadAppImage = styled.img`
  height: 40px;
`;

function OutcomeApproved({ storeState }) {
  const { gemVisaAccountNumber, applicationRef, accountNumber } = storeState;

  const auCard = getCardImageMobile();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const creditLimit = displayCreditLimit(storeState);
  const { shoppingInformation } = OutcomeApprovedSourcesAu;

  const nzRatesAndFees = getRatesAndFees();
  const { whatHappensNextStepsAu, shopOnlineAndInStoreAu } = getAuCardOutcomeApprovedNextSteps(
    storeState,
    trackEventSender,
  );

  const OutcomeApprovedNZV2 = () => {
    const productName = getProductName();

    return (
      <Container margin="0 0 13 0" hasBreakpointWidth>
        <Container padding="5 0" padding-md="5" testId="LAYOUT FORM">
          <Grid preset="page" debug={false}>
            <Grid.Item colsGap="2" colSpan="all" colSpan-sm="1-6" colSpan-md="1-6" colSpan-lg="2-9" colSpan-xl="3-8">
              <Grid rowsGap="6">
                <Container padding="1 0 0 0" padding-lg="8 0 0 0">
                  <Text as="h1" textStyle="heading4" textStyle-md="heading3" testId="congratulations">
                    Congratulations!
                  </Text>
                  <Text />
                </Container>
                <SmallContainerWrapper>
                  <Container padding="8" padding-lg="10">
                    <Grid rowsGap="4">
                      <Grid.Item colSpan="all" colSpan-lg="0-5" rowSpan-md="0">
                        <TopImageWrapper productCode={config.productCode}>
                          <img src={gemNzCard} alt="NZV2 Card" />
                        </TopImageWrapper>
                      </Grid.Item>
                      <Grid.Item colSpan="all" colSpan-lg="6-11" rowSpan="1" rowSpan-lg="0">
                        <TextAlignWrapper>
                          <Text as="h2" textStyle="heading5" align="center">
                            <>
                              You're approved for the {productName} with a ${creditLimit} credit limit
                            </>
                          </Text>
                        </TextAlignWrapper>
                      </Grid.Item>
                    </Grid>
                  </Container>
                </SmallContainerWrapper>
                <Container>
                  <Text>
                    Account number: <b>{accountNumber || gemVisaAccountNumber}</b>
                  </Text>
                </Container>
                <Stack gap="4">
                  <Container margin="0 0 0 4">
                    <Text as="h3" textStyle="heading5">
                      What happens next?
                    </Text>
                  </Container>
                  {whatHappensNextStepsNzV2(nzRatesAndFees).map(({ icon, label }, index) => (
                    <IconTextWrapper key={label + index}>
                      {icon}
                      <RightTextWrapper>
                        <Text>{label}</Text>
                      </RightTextWrapper>
                    </IconTextWrapper>
                  ))}
                </Stack>
                <Container bg="grey.t03" margin="0 2 0 0" margin-xs={0} padding="0 0 4 0">
                  {shopOnlineAndInStoreAu.boxes.map(({ heading, steps }, index) => (
                    <>
                      {index !== 0 &&
                        shopOnlineAndInStoreAu.boxes.length > 1 &&
                        shopOnlineAndInStoreAu.showSeparator && <DividerWithText data-content="or" />}
                      <Container key={`shopOnlineAndInStoreAu.boxes.ShadowedContainer ${index}`} padding="4 4 0 4">
                        <ShadowedContainer>
                          <Container bg="white" padding="6">
                            <ImageHeaderWrapper>
                              <img src={heading.imageSrc} alt={heading.altText} />
                              <TextHeaderWrapper>
                                <Text as="h3" textStyle="heading5">
                                  {heading.text}
                                </Text>
                              </TextHeaderWrapper>
                            </ImageHeaderWrapper>
                            {steps.map(
                              (
                                { title, subTitle, list, showAppDownloadButtons, showAppleGoogleSamsungPay, ctaButton },
                                idx,
                              ) => (
                                <React.Fragment key={`steps ${idx}`}>
                                  <NumberedListWrapper>
                                    <StyledNumberListItem style={{ background: ellipse }}>
                                      {idx + 1}
                                    </StyledNumberListItem>
                                    <NumberedTextWrapper>
                                      <StyledStrong>{title}</StyledStrong>
                                    </NumberedTextWrapper>
                                  </NumberedListWrapper>
                                  {subTitle && <Text margin="0 0 0 9">{subTitle}</Text>}
                                  <StyledUL marginLeft="24px" marginTop="4px" marginBottom="8px">
                                    {list.map(({ title: listItemTitle, text }, listIdx) => (
                                      <li key={`steps.list ${idx} ${listIdx}`}>
                                        {listItemTitle && <StyledStrong>{listItemTitle}</StyledStrong>} {text}
                                      </li>
                                    ))}
                                  </StyledUL>
                                  {showAppDownloadButtons && (
                                    <Container margin="1 0 2 10" padding="3 0 4 3">
                                      <Flex width="100%">
                                        <Container>
                                          <Link appearance="icon" href={shoppingInformation.apple_store.url} newTab>
                                            <DownloadAppImage
                                              src={DownloadApple}
                                              onClick={() =>
                                                trackEventSender({
                                                  location: shoppingInformation.apple_store.location,
                                                  label: shoppingInformation.apple_store.label,
                                                })
                                              }
                                            />
                                          </Link>
                                        </Container>
                                        <Container>
                                          <Link appearance="icon" href={shoppingInformation.google_store.url} newTab>
                                            <DownloadAppImage
                                              src={DownloadGoogle}
                                              onClick={() =>
                                                trackEventSender({
                                                  location: shoppingInformation.google_store.location,
                                                  label: shoppingInformation.google_store.label,
                                                })
                                              }
                                            />
                                          </Link>
                                        </Container>
                                      </Flex>
                                    </Container>
                                  )}
                                  {showAppleGoogleSamsungPay && (
                                    <Container margin="1 0 2 10" padding="3 0 4 3">
                                      <img src={appleGoogleSamsungPay} alt="Apple Google and Samsung Pay" />
                                    </Container>
                                  )}
                                  {ctaButton && <div>{ctaButton}</div>}
                                  {index !== shopOnlineAndInStoreAu.boxes.length - 1 && <br />}
                                </React.Fragment>
                              ),
                            )}
                          </Container>
                        </ShadowedContainer>
                      </Container>
                    </>
                  ))}
                </Container>
              </Grid>
            </Grid.Item>
          </Grid>
        </Container>
      </Container>
    );
  };

  const OutcomeApprovedAU = () => {
    const productName = getProductName();
    const ratesAndFees = getRatesAndFees();

    return (
      <Container margin="0 0 13 0" hasBreakpointWidth>
        <Container padding="5 0" padding-md="5" testId="LAYOUT FORM">
          <Grid preset="page" debug={false}>
            <Grid.Item colsGap="2" colSpan="all" colSpan-sm="1-6" colSpan-md="1-6" colSpan-lg="2-9" colSpan-xl="3-8">
              <Grid rowsGap="6">
                <Container padding="1 0 0 0" padding-lg="8 0 0 0">
                  <Text as="h1" textStyle="heading4" textStyle-sm="heading3" testId="congratulations">
                    Congratulations!
                  </Text>
                  <Text />
                </Container>
                <SmallContainerWrapper>
                  <Container padding="8" padding-lg="10">
                    <Grid rowsGap="4">
                      <Grid.Item colSpan="all" colSpan-lg="0-5" rowSpan-md="0">
                        <TopImageWrapper productCode={config.productCode}>
                          <img src={auCard} alt="AU Card" />
                        </TopImageWrapper>
                      </Grid.Item>
                      <Grid.Item colSpan="all" colSpan-lg="6-11" rowSpan="1" rowSpan-lg="0">
                        <TextAlignWrapper>
                          <Text as="h2" textStyle="heading5" align="center">
                            {isDJ() ? (
                              <>
                                You're approved for ${creditLimit} {productName} credit card
                              </>
                            ) : (
                              <>
                                You're approved for the {productName}{' '}
                                {byCard({
                                  [PRODUCT_CODE_DAVID_JONES_PREMIERE]: 'credit card',
                                  [PRODUCT_CODE_DAVID_JONES_PRESTIGE]: 'credit card',
                                  default: '',
                                })}{' '}
                                with a ${creditLimit} credit limit
                              </>
                            )}
                          </Text>
                        </TextAlignWrapper>
                      </Grid.Item>
                    </Grid>
                  </Container>
                </SmallContainerWrapper>
                <Container>
                  <Text>
                    {isDJ() ? (
                      <>
                        Application reference number: <b>{applicationRef}</b>
                      </>
                    ) : (
                      <>
                        Account number: <b>{accountNumber}</b>
                      </>
                    )}
                  </Text>
                </Container>
                <Stack gap="4">
                  <Container margin="0 0 0 4">
                    <Text as="h3" textStyle="heading5">
                      What happens next?
                    </Text>
                  </Container>
                  {whatHappensNextStepsAu.map(({ icon, label }, index) => (
                    <IconTextWrapper key={label + index}>
                      {icon}
                      <RightTextWrapper>
                        <Text>{label}</Text>
                      </RightTextWrapper>
                    </IconTextWrapper>
                  ))}
                </Stack>
                <Container bg="grey.t03" margin="0 2 0 0" margin-xs={0} padding="0 0 4 0">
                  {shopOnlineAndInStoreAu.boxes.map(({ heading, steps }, index) => (
                    <>
                      {index !== 0 &&
                        shopOnlineAndInStoreAu.boxes.length > 1 &&
                        shopOnlineAndInStoreAu.showSeparator && <DividerWithText data-content="or" />}
                      <Container key={`shopOnlineAndInStoreAu.boxes.ShadowedContainer ${index}`} padding="4 4 0 4">
                        <ShadowedContainer>
                          <Container bg="white" padding="6">
                            <ImageHeaderWrapper>
                              <img src={heading.imageSrc} alt={heading.altText} />
                              <TextHeaderWrapper>
                                <Text as="h3" textStyle="heading5">
                                  {heading.text}
                                </Text>
                              </TextHeaderWrapper>
                            </ImageHeaderWrapper>
                            {steps.map(
                              (
                                { title, subTitle, list, showAppDownloadButtons, showAppleGoogleSamsungPay, ctaButton },
                                idx,
                              ) => (
                                <React.Fragment key={`steps ${idx}`}>
                                  <NumberedListWrapper>
                                    <StyledNumberListItem style={{ background: ellipse }}>
                                      {idx + 1}
                                    </StyledNumberListItem>
                                    <NumberedTextWrapper>
                                      <StyledStrong>{title}</StyledStrong>
                                    </NumberedTextWrapper>
                                  </NumberedListWrapper>
                                  {subTitle && <Text margin="0 0 0 9">{subTitle}</Text>}
                                  <StyledUL marginLeft="24px" marginTop="4px" marginBottom="8px">
                                    {list.map(({ title: listItemTitle, text }, listIdx) => (
                                      <li key={`steps.list ${idx} ${listIdx}`}>
                                        {listItemTitle && <StyledStrong>{listItemTitle}</StyledStrong>} {text}
                                      </li>
                                    ))}
                                  </StyledUL>
                                  {showAppDownloadButtons && (
                                    <Container margin="1 0 2 10" padding="3 0 4 3">
                                      <Flex width="100%">
                                        <Container>
                                          <Link appearance="icon" href={shoppingInformation.apple_store.url} newTab>
                                            <DownloadAppImage
                                              src={DownloadApple}
                                              onClick={() =>
                                                trackEventSender({
                                                  location: shoppingInformation.apple_store.location,
                                                  label: shoppingInformation.apple_store.label,
                                                })
                                              }
                                            />
                                          </Link>
                                        </Container>
                                        <Container>
                                          <Link appearance="icon" href={shoppingInformation.google_store.url} newTab>
                                            <DownloadAppImage
                                              src={DownloadGoogle}
                                              onClick={() =>
                                                trackEventSender({
                                                  location: shoppingInformation.google_store.location,
                                                  label: shoppingInformation.google_store.label,
                                                })
                                              }
                                            />
                                          </Link>
                                        </Container>
                                      </Flex>
                                    </Container>
                                  )}
                                  {showAppleGoogleSamsungPay && (
                                    <Container margin="1 0 2 10" padding="3 0 4 3">
                                      <img src={appleGoogleSamsungPay} alt="Apple Google and Samsung Pay" />
                                    </Container>
                                  )}
                                  {ctaButton && <div>{ctaButton}</div>}
                                  {index !== shopOnlineAndInStoreAu.boxes.length - 1 && <br />}
                                </React.Fragment>
                              ),
                            )}
                          </Container>
                        </ShadowedContainer>
                      </Container>
                    </>
                  ))}
                </Container>
                {isGemAU() && (
                  <Text textStyle="legal">
                    ^To take advantage of up to 55 days Interest Free on every day credit card purchases, you need to
                    pay the full closing balance (excluding un-expiring {ratesAndFees.interestRate}% Interest Payment
                    Plans) on each statement of account by the applicable due date.
                  </Text>
                )}
              </Grid>
            </Grid.Item>
          </Grid>
        </Container>
      </Container>
    );
  };
  return byCountry({
    AU: <OutcomeApprovedAU />,
    NZ: <OutcomeApprovedNZV2 />,
  });
}

export { OutcomeApproved };
